import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { AlertContext } from "@actinc/dls/context";
import { Dialog, GridGenerator, FormInput } from "@actinc/dls";

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Paper,
  Typography,
} from "@mui/material";
import { useClient } from "urql";
import { useForm, useFormState } from "react-hook-form";
import { useParams } from "react-router-dom";

import { FileDelimitedOutline as ChooseFileIcon } from "@actinc/dls/icons";

import {
  Backdrop,
  ControlledFormSelect,
  DataGrid,
  SimpleTable,
} from "../../../components";

import { ProgramsContext } from "../../../programsProvider";
import { UserContext } from "../../../userProvider";
import { UsersContext } from "../../../usersProvider";
import { MetadataConfigurationContext } from "../../../metadataConfigurationProvider";

import { getRHFFieldOptionLabel } from "../../../services/formUtils";
import {
  getSelectOptionsFromMetadata,
  getUserOptions,
  setTitle,
} from "../../../services/utils";
import _ from "lodash/fp";
import { groupImportEventsByBatch } from "../ImportContent/importContentUtils";
import { useImportPackage } from "./hooks/useImportPackage";
import {
  getValidationErrorMessage,
  IMPORT_TYPE_OPTIONS,
  IMPORT_TYPES,
} from "./jsonImportUtils";

const DEFAULT_DEVELOPMENT_STATE = "Content Review 2";

const IMPORT_RESULT_COLUMNS = [
  { title: "Type", identifier: "type" },
  { title: "Name", identifier: "name" },
  { title: "Status", identifier: "status" },
  { title: "Details", identifier: "details" },
];

const IMPORT_RESULT_GRID_COLUMNS = [
  { field: "type", flex: 1, headerName: "Type", type: "string" },
  { field: "name", flex: 1, headerName: "Name", type: "string" },
  { field: "status", flex: 1, headerName: "Status", type: "string" },
  { field: "details", flex: 2, headerName: "Details", type: "string" },
];

const IMPORT_HISTORY_GRID_COLUMNS = [
  {
    field: "startTime",
    flex: 1,
    headerName: "Start Time",
    type: "dateTime",
    valueGetter: ({ value }) => value && new Date(value),
  },
  { field: "programName", flex: 1, headerName: "Program", type: "string" },
  { field: "fileName", flex: 1, headerName: "File Name", type: "string" },
  { field: "type", flex: 1, headerName: "Type", type: "string" },
  { field: "itemCount", flex: 1, headerName: "Item Count", type: "number" },
  {
    field: "passageCount",
    flex: 1,
    headerName: "Passage Count",
    type: "number",
  },
  { field: "user", flex: 1, headerName: "User", type: "string" },
];

const IMPORT_HISTORY_SORT_MODEL = {
  sorting: { sortModel: [{ field: "startTime", sort: "desc" }] },
};

const ImportContent = () => {
  const { objectType } = useParams();
  const inputRef = useRef(null);
  const client = useClient();
  const { importPackage, validatePackage } = useImportPackage(client);

  const defaultAlert = {
    message: "",
    severity: "error",
    title: "Upload Error",
  };
  const [alertMessages, setAlertMessages] = useState(defaultAlert);
  const [dialogState, setDialogState] = useState({
    message: "",
    open: false,
  });
  const [file, setFile] = useState(null);
  const [importResultsDialog, setImportResultsDialog] = useState({
    open: false,
    results: [],
  });
  const [loading, setLoading] = useState(false);
  const [loadingImportHistory, setLoadingImportHistory] = useState(false);
  const [validatedData, setValidatedData] = useState({
    data: [],
    errorMessage: "",
    existingObjects: [],
  });

  const [validFileType, setValidFileType] = useState(false);

  const alertContext = useContext(AlertContext);
  const allPrograms = useContext(ProgramsContext);
  const user = useContext(UserContext);
  const allUsers = useContext(UsersContext);
  const metadataConfiguration = useContext(MetadataConfigurationContext);

  const userAccessiblePrograms = useMemo(
    () => allPrograms.filter(({ _actions }) => _actions.includes("import")),
    [allPrograms],
  );

  const [importHistory, setImportHistory] = useState([]);

  const { control, reset, resetField, watch } = useForm({
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: {
      contentArea: "",
      contentSpecialist: "",
      developmentState: DEFAULT_DEVELOPMENT_STATE,
      itemWriter: "",
      program: "",
      type: IMPORT_TYPES.QTI,
    },
  });

  const { isSubmitSuccessful } = useFormState({ control });

  // to be called to update the list of imports after an import has been completed
  const refreshImportHistory = useCallback(async () => {
    setLoadingImportHistory(true);
    const results = await client.query(getImportEvents).toPromise();
    if (!results?.data?.importEvents) {
      setLoadingImportHistory(false);
      return;
    }
    const groupedImportEvents = groupImportEventsByBatch(
      results.data.importEvents,
    );
    const historyArray = groupedImportEvents.map((element) => ({
      id: element.id,
      startTime: new Date(element.startTime).toLocaleString(),
      programName: allPrograms.find((program) => element.program === program.id)
        ?.name,
      fileName: element.fileName,
      itemCount: element.itemCount,
      passageCount: element.passageCount,
      results: element.results ?? [],
      type: element.type ?? IMPORT_TYPES.QTI,
      user: allUsers.find((user) => element.user === user.id)?.name,
    }));
    setImportHistory(historyArray);
    setLoadingImportHistory(false);
  }, [allPrograms, allUsers, client]);

  useEffect(() => {
    setTitle(["CDE", "Import Content"]);
    refreshImportHistory();
  }, [refreshImportHistory]);

  const handleResets = useCallback(() => {
    reset({
      contentArea: "",
      contentSpecialist: "",
      developmentState: DEFAULT_DEVELOPMENT_STATE,
      itemWriter: "",
      program: "",
      type: IMPORT_TYPES.QTI,
    });
    setAlertMessages([]);
    setFile(null);
    setValidatedData({
      data: [],
      errorMessage: "",
    });
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  }, [reset]);

  useEffect(() => {
    handleResets();
  }, [objectType, handleResets]);

  const selectedProgram = watch("program");
  const selectedContentArea = watch("contentArea");
  const selectedContentSpecialist = watch("contentSpecialist");
  const selectedDevelopmentState = watch("developmentState");
  const selectedItemWriter = watch("itemWriter");
  const selectedType = watch("type");

  // Only Super Admins can import JSON data packages; everyone else imports QTI
  const canImportJson = !!user?.superAdmin;
  const importType = canImportJson ? selectedType : IMPORT_TYPES.QTI;

  const contentSpecialistOptions = useMemo(
    () =>
      getUserOptions(allUsers)({
        activeOnly: true,
        programs: [selectedProgram],
        roles: ["Content Specialist"],
      }),
    [allUsers, selectedProgram],
  );

  const itemWriterOptions = useMemo(
    () =>
      getUserOptions(allUsers)({
        activeOnly: true,
        programs: [selectedProgram],
        roles: ["Item Writer"],
      }),
    [allUsers, selectedProgram],
  );

  const contentAreaOptions = useMemo(() => {
    if (!metadataConfiguration || !selectedProgram) return [];
    const programsForMetadata = userAccessiblePrograms
      .filter(({ id }) => id === selectedProgram)
      .map(({ configurationID, id: value }) => ({ configurationID, value }));
    const values =
      getSelectOptionsFromMetadata(metadataConfiguration)(programsForMetadata)(
        "item",
      )("contentArea") ?? [];
    return values.map((obj) => ({
      label: obj?.stringValue,
      value: obj?.stringValue,
    }));
  }, [metadataConfiguration, selectedProgram, userAccessiblePrograms]);

  const developmentStateOptions = useMemo(() => {
    if (!metadataConfiguration) return [];
    const programsForMetadata = userAccessiblePrograms.map(
      ({ configurationID, id: value }) => ({ configurationID, value }),
    );
    const values =
      getSelectOptionsFromMetadata(metadataConfiguration)(programsForMetadata)(
        "item",
      )("developmentState") ?? [];
    return _.uniqBy((obj) => obj?.stringValue)(values).map((obj) => ({
      label: obj?.stringValue,
      value: obj?.stringValue,
    }));
  }, [metadataConfiguration, userAccessiblePrograms]);

  useEffect(() => {
    resetField("contentArea", { defaultValue: "" });
    resetField("contentSpecialist", { defaultValue: "" });
    resetField("itemWriter", { defaultValue: "" });
  }, [selectedProgram, resetField]);

  const closeImportResultsDialog = useCallback(
    () => setImportResultsDialog({ open: false, results: [] }),
    [],
  );

  const handleViewImportResults = useCallback(
    (row) => () =>
      setImportResultsDialog({ open: true, results: row.results ?? [] }),
    [],
  );

  const importResultsDialogProps = useMemo(
    () => ({
      draggable: false,
      fullWidth: true,
      onClose: closeImportResultsDialog,
      open: importResultsDialog.open,
      scroll: "paper",
      title: `Import Results`,
    }),
    [closeImportResultsDialog, importResultsDialog.open],
  );

  const importResultsDataGridProps = useMemo(
    () => ({
      columns: IMPORT_RESULT_GRID_COLUMNS,
      error: false,
      rows: importResultsDialog.results.map((result, id) => ({
        ...result,
        id,
      })),
    }),
    [importResultsDialog.results],
  );

  const dataGridProps = useMemo(
    () => ({
      actions: [{ label: "View Results", onClick: handleViewImportResults }],
      columns: IMPORT_HISTORY_GRID_COLUMNS,
      initialState: IMPORT_HISTORY_SORT_MODEL,
      loading: loadingImportHistory,
      rows: importHistory,
    }),
    [handleViewImportResults, importHistory, loadingImportHistory],
  );

  if (!userAccessiblePrograms?.length) return null;

  const contentSpecialistInput = selectedContentSpecialist
    ? {
        id: selectedContentSpecialist,
        name: getRHFFieldOptionLabel(contentSpecialistOptions)(
          selectedContentSpecialist,
        ),
      }
    : null;

  const itemWriterInput = selectedItemWriter
    ? {
        id: selectedItemWriter,
        name: getRHFFieldOptionLabel(itemWriterOptions)(selectedItemWriter),
      }
    : null;

  const closeDialog = () => {
    setDialogState({ open: false, message: "" });
    handleResets();
  };

  const fileInputOnChange = (e) => {
    const fileName = e.target.files[0].name;
    const filetype = fileName.substring(
      fileName.lastIndexOf(".") + 1,
      fileName.length,
    );
    setValidFileType(filetype === "zip");
    setAlertMessages(defaultAlert);
    setFile(e.target.files[0]);
    setValidatedData({
      data: [],
      errorMessage: "",
    });
    reset({
      contentArea: selectedContentArea,
      contentSpecialist: selectedContentSpecialist,
      developmentState: selectedDevelopmentState,
      itemWriter: selectedItemWriter,
      program: selectedProgram,
      type: selectedType,
    });
  };

  const fileInputOnClick = (e) => {
    inputRef.current.value = "";
    inputRef.current.click();
    setAlertMessages(defaultAlert);
    setFile(null);
    setValidatedData({
      data: [],
      errorMessage: "",
    });
  };

  const importMetadata = {
    contentArea: selectedContentArea,
    contentSpecialist: contentSpecialistInput,
    developmentState: selectedDevelopmentState,
    itemWriter: itemWriterInput,
  };

  const handleValidation = async () => {
    setLoading(true);
    try {
      const data = await validatePackage({
        file,
        importType,
        metadata: importMetadata,
        program: selectedProgram,
      });
      setValidatedData({ data, errorMessage: "" });
    } catch (e) {
      setValidatedData((prevState) => ({
        ...prevState,
        errorMessage: getValidationErrorMessage({
          canImportJson,
          error: e,
          importType,
        }),
      }));
    } finally {
      setDialogState({
        open: true,
      });
      setLoading(false);
    }
  };

  const handleImport = async () => {
    closeDialog();
    if (validatedData.errorMessage) return;

    setLoading(true);
    try {
      await importPackage({
        file,
        importType,
        metadata: importMetadata,
        program: selectedProgram,
        validationResults: validatedData.data,
      });

      alertContext.actions.addAlert({
        message: `Import successful`,
        options: { variant: "success" },
      });
    } catch (e) {
      alertContext.actions.addAlert({
        message: "Import failed",
        options: { variant: "error" },
      });
    }

    await refreshImportHistory();
    setLoading(false);
  };

  const dialogProps = {
    draggable: false,
    fullWidth: true,
    onClose: closeDialog,
    open: dialogState.open,
    scroll: "paper",
    title: `Import Preview`,
  };

  const tableProps = {
    columns: IMPORT_RESULT_COLUMNS,
    error: false,
    rows: validatedData?.data.length
      ? validatedData.data.map((d, id) => ({ ...d, id }))
      : [],
  };

  return (
    <>
      <Backdrop loading={loading} />
      <Paper
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          margin: 2,
          padding: 1,
        }}
      >
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h4">{`Import Content`}</Typography>
        </Box>
        <form id="importContentForm">
          <Box
            sx={{
              alignItems: "end",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <GridGenerator
              groups={[
                [
                  {
                    children: (
                      <ControlledFormSelect
                        control={control}
                        fullWidth={true}
                        id="import-content-program"
                        label="Program"
                        name="program"
                        options={userAccessiblePrograms?.map((value) => ({
                          label: value?.name,
                          value: value?.id,
                        }))}
                        rules={{ required: true }}
                      />
                    ),
                    key: "import-content-program",
                  },
                  ...(canImportJson
                    ? [
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              fullWidth={true}
                              id="import-content-type"
                              label="Type"
                              name="type"
                              options={IMPORT_TYPE_OPTIONS}
                              rules={{ required: true }}
                            />
                          ),
                          key: "import-content-type",
                        },
                      ]
                    : []),
                ],
                [
                  {
                    children: (
                      <ControlledFormSelect
                        control={control}
                        disabled={!selectedProgram}
                        fullWidth={true}
                        id="import-content-content-specialist"
                        label="Content Specialist"
                        name="contentSpecialist"
                        options={contentSpecialistOptions}
                      />
                    ),
                    key: "import-content-content-specialist",
                  },
                  {
                    children: (
                      <ControlledFormSelect
                        control={control}
                        disabled={!selectedProgram}
                        fullWidth={true}
                        id="import-content-item-writer"
                        label="Item Writer"
                        name="itemWriter"
                        options={itemWriterOptions}
                      />
                    ),
                    key: "import-content-item-writer",
                  },
                ],
                [
                  {
                    children: (
                      <ControlledFormSelect
                        control={control}
                        disabled={!selectedProgram}
                        fullWidth={true}
                        id="import-content-content-area"
                        label="Content Area"
                        name="contentArea"
                        options={contentAreaOptions}
                      />
                    ),
                    key: "import-content-content-area",
                  },
                  {
                    children: (
                      <ControlledFormSelect
                        control={control}
                        fullWidth={true}
                        id="import-content-development-state"
                        label="Development State"
                        name="developmentState"
                        options={developmentStateOptions}
                      />
                    ),
                    key: "import-content-development-state",
                  },
                ],
                [
                  {
                    children: (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "start",
                            marginBottom: 1,
                          }}
                        >
                          <Button
                            disabled={!selectedProgram}
                            onClick={fileInputOnClick}
                            size="small"
                            startIcon={<ChooseFileIcon />}
                            variant="outlined"
                          >
                            Choose File
                          </Button>
                        </Box>
                        <input
                          accept=".zip"
                          aria-label="Choose File"
                          id="csv-file-input"
                          onChange={fileInputOnChange}
                          ref={inputRef}
                          style={{ display: "none" }}
                          type="file"
                        />
                        <Typography>{file && file.name}</Typography>
                        {!validFileType && file && (
                          <Typography
                            sx={{
                              color: "#db485f",
                              fontWeight: "medium",
                              mt: 2,
                            }}
                          >
                            Invalid file type
                          </Typography>
                        )}
                      </>
                    ),
                    key: "content-csv-file",
                  },
                ],
              ]}
            ></GridGenerator>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              <Button
                disabled={!selectedProgram || !file}
                onClick={handleValidation}
                sx={{ marginRight: 2 }}
                variant="contained"
              >
                Validate
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
      <Paper
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          margin: 2,
          padding: 1,
        }}
      >
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="h5">{`Import History`}</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexGrow: 1,
            minHeight: "500px",
          }}
        >
          <DataGrid {...dataGridProps} />
        </Box>
      </Paper>

      {alertMessages?.message && (
        <Box sx={{ marginBottom: 2 }}>
          <Alert severity={alertMessages?.severity}>
            <AlertTitle>{alertMessages?.title}</AlertTitle>
            {alertMessages?.message}
          </Alert>
        </Box>
      )}

      <Dialog {...dialogProps}>
        <DialogContent
          sx={{
            marginTop: 1,
            paddingBottom: 5,
            marginBottom: 5,
          }}
        >
          <GridGenerator
            groups={[
              [
                {
                  children: (
                    <FormInput
                      fullWidth={true}
                      id="import-content-program"
                      label="Program"
                      name="program"
                      readOnly
                      value={
                        userAccessiblePrograms.find(
                          (program) => program.id === selectedProgram,
                        )?.name
                      }
                    />
                  ),
                  key: "import-content-program",
                },
                {
                  children: (
                    <FormInput
                      fullWidth={true}
                      id="import-content-content-specialist"
                      label="Content Specialist"
                      name="contentSpecialist"
                      readOnly
                      value={
                        getRHFFieldOptionLabel(contentSpecialistOptions)(
                          selectedContentSpecialist,
                        ) ?? ""
                      }
                    />
                  ),
                  key: "import-content-content-specialist",
                },
                {
                  children: (
                    <FormInput
                      fullWidth={true}
                      id="import-content-item-writer"
                      label="Item Writer"
                      name="itemWriter"
                      readOnly
                      value={
                        getRHFFieldOptionLabel(itemWriterOptions)(
                          selectedItemWriter,
                        ) ?? ""
                      }
                    />
                  ),
                  key: "import-content-item-writer",
                },
                {
                  children: (
                    <FormInput
                      fullWidth={true}
                      id="import-content-content-area"
                      label="Content Area"
                      name="contentArea"
                      readOnly
                      value={selectedContentArea ?? ""}
                    />
                  ),
                  key: "import-content-content-area",
                },
                {
                  children: (
                    <FormInput
                      fullWidth={true}
                      id="import-content-development-state"
                      label="Development State"
                      name="developmentState"
                      readOnly
                      value={selectedDevelopmentState ?? ""}
                    />
                  ),
                  key: "import-content-development-state",
                },
                {
                  children: (
                    <FormInput
                      fullWidth={true}
                      id="filename"
                      label="Filename"
                      name="filename"
                      readOnly
                      value={file?.name}
                    />
                  ),
                  key: "import-content-filename",
                },
              ],
            ]}
          ></GridGenerator>
        </DialogContent>
        <DialogContent>
          <Typography variant="subtitle2">
            {validatedData.errorMessage}
          </Typography>
          {!validatedData.errorMessage && (
            <SimpleTable {...tableProps}></SimpleTable>
          )}
        </DialogContent>
        <DialogActions>
          {validatedData?.data.every((item) => item.status !== "Invalid") &&
            !validatedData.errorMessage &&
            !isSubmitSuccessful && (
              <Button onClick={closeDialog} variant="text">
                Cancel
              </Button>
            )}
          <Button onClick={handleImport} variant="text">
            {!!validatedData.errorMessage ||
            validatedData?.data.some((item) => item.status === "Invalid")
              ? "Close"
              : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog {...importResultsDialogProps}>
        <DialogContent>
          {importResultsDialog.results.length ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexGrow: 1,
                minHeight: "400px",
              }}
            >
              <DataGrid {...importResultsDataGridProps} />
            </Box>
          ) : (
            <Typography variant="subtitle2">
              No detailed results were recorded for this import.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeImportResultsDialog} variant="text">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const getImportEvents = `
  query getImportEvents {
    importEvents {
      id
      batchId
      startTime
      program
      fileName
      itemCount
      passageCount
      type
      results {
        details
        name
        status
        type
      }
      user
    }
  }`;

export { getImportEvents, ImportContent };
